import { useState } from 'react'
import { Link, useNavigate, useSearchParams } from 'react-router-dom'
import { useBrand } from '@/brand/BrandProvider'
import { apiClient } from '@/api/client'
import { formatApiError } from '@/utils/httpErrors'
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Button } from '@/components/ui/button'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { Eye, EyeOff } from 'lucide-react'
import { PRODUCT, PRODUCT_KEY } from '@/lib/productIdentity'

const MIN_PASSWORD_LENGTH = 8

export function ResetPassword() {
  const { theme } = useBrand()
  // PRODUCT-IDENTITY-1 (I3): preserve QoE's exact pre-existing text ("QoE
  // Platform"), same reasoning as TosGate.tsx — see that file's comment.
  const productName = PRODUCT_KEY === 'qoe' ? 'QoE Platform' : PRODUCT.headline
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const token = searchParams.get('token') ?? ''

  const [newPassword, setNewPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [showNew, setShowNew] = useState(false)
  const [showConfirm, setShowConfirm] = useState(false)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError(null)

    if (newPassword.length < MIN_PASSWORD_LENGTH) {
      setError(`Password must be at least ${MIN_PASSWORD_LENGTH} characters.`)
      return
    }
    if (newPassword !== confirmPassword) {
      setError('Passwords do not match.')
      return
    }

    setLoading(true)
    try {
      await apiClient.post('/auth/reset-password', { token, new_password: newPassword })
      navigate('/login', { replace: true, state: { resetSuccess: true } })
    } catch (err: unknown) {
      const status = (err as { response?: { status: number } })?.response?.status
      if (status === 400) setError('This reset link is invalid, expired, or already used.')
      else setError(formatApiError(err))
    } finally {
      setLoading(false)
    }
  }

  return (
    <div
      className="min-h-screen flex flex-col items-center justify-center p-4"
      style={{ backgroundColor: 'var(--color-bg)' }}
    >
      <img
        src={`/api/brand/${theme.key}/logo`}
        alt={theme.name}
        className="h-10 mb-6 object-contain"
        onError={(e) => { (e.target as HTMLImageElement).style.display = 'none' }}
      />

      <Card className="w-full max-w-md shadow-lg">
        <CardHeader className="space-y-1">
          <CardTitle
            className="text-2xl font-bold"
            style={{ color: 'var(--color-primary)' }}
          >
            Reset password
          </CardTitle>
          <CardDescription>{theme.name} · {productName}</CardDescription>
        </CardHeader>

        <CardContent className="space-y-4">
          {!token && (
            <Alert variant="destructive">
              <AlertDescription>
                This reset link is missing its token. Please request a new one.
              </AlertDescription>
            </Alert>
          )}

          {error && (
            <Alert variant="destructive">
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}

          <form onSubmit={handleSubmit} className="space-y-3">
            <div className="space-y-1">
              <Label htmlFor="newPassword">New password</Label>
              <div className="relative">
                <Input
                  id="newPassword" type={showNew ? 'text' : 'password'}
                  value={newPassword} onChange={e => setNewPassword(e.target.value)}
                  required autoComplete="new-password" autoFocus
                  disabled={!token} className="pr-10"
                />
                <button
                  type="button"
                  onClick={() => setShowNew(v => !v)}
                  aria-label={showNew ? 'Hide password' : 'Show password'}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500"
                >
                  {showNew ? <EyeOff size={16} /> : <Eye size={16} />}
                </button>
              </div>
            </div>
            <div className="space-y-1">
              <Label htmlFor="confirmPassword">Confirm new password</Label>
              <div className="relative">
                <Input
                  id="confirmPassword" type={showConfirm ? 'text' : 'password'}
                  value={confirmPassword} onChange={e => setConfirmPassword(e.target.value)}
                  required autoComplete="new-password"
                  disabled={!token} className="pr-10"
                />
                <button
                  type="button"
                  onClick={() => setShowConfirm(v => !v)}
                  aria-label={showConfirm ? 'Hide password' : 'Show password'}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500"
                >
                  {showConfirm ? <EyeOff size={16} /> : <Eye size={16} />}
                </button>
              </div>
            </div>
            <Button
              type="submit" className="w-full" disabled={loading || !token}
              style={{ backgroundColor: 'var(--color-primary)' }}
            >
              {loading ? 'Resetting…' : 'Reset password'}
            </Button>
          </form>

          <div className="text-center text-sm text-gray-500 pt-2 space-y-1">
            <p>
              <Link to="/forgot-password" className="font-medium underline"
                    style={{ color: 'var(--color-primary)' }}>
                Request a new reset link
              </Link>
            </p>
            <p>
              <Link to="/login" className="font-medium underline"
                    style={{ color: 'var(--color-primary)' }}>
                ← Back to sign in
              </Link>
            </p>
          </div>
        </CardContent>
      </Card>
    </div>
  )
}
